/**
 * Authentication API service
 */

import { apiRequest } from './api';
import type {
  LoginRequest,
  RegisterRequest,
  AuthResponse,
  User,
  WsTokenResponse,
  UserUpdateRequest,
  RefreshRequest,
  LogoutRequest,
} from '../types/auth';

/**
 * Register a new user account
 */
export async function register(data: RegisterRequest): Promise<AuthResponse> {
  return apiRequest<AuthResponse>('/api/auth/register', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

/**
 * Login with username and password
 */
export async function login(data: LoginRequest): Promise<AuthResponse> {
  return apiRequest<AuthResponse>('/api/auth/login', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

/**
 * Validate current session
 */
export async function validateSession(): Promise<User> {
  return apiRequest<User>('/api/auth/validate');
}

/**
 * Get WebSocket authentication token
 */
export async function getWsToken(): Promise<string> {
  const response = await apiRequest<WsTokenResponse>('/api/auth/ws-token');
  return response.ws_token;
}

/**
 * Get current user profile
 */
export async function getUserProfile(): Promise<User> {
  return apiRequest<User>('/api/users/me');
}

/**
 * Update current user profile
 */
export async function updateUserProfile(data: UserUpdateRequest): Promise<User> {
  return apiRequest<User>('/api/users/me', {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

/**
 * Refresh access token using refresh token
 */
export async function refreshToken(data: RefreshRequest): Promise<AuthResponse> {
  return apiRequest<AuthResponse>('/api/auth/refresh', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

/**
 * Logout and revoke tokens
 */
export async function logout(data?: LogoutRequest): Promise<{ message: string }> {
  return apiRequest<{ message: string }>('/api/auth/logout', {
    method: 'POST',
    body: JSON.stringify(data || {}),
  });
}
